import { get, post } from '../utils/request';
import { AuthResponse, UserInfo } from '../utils/auth';

// Re-export UserInfo for convenience
export type { UserInfo };

// Login request interface
export interface LoginRequest {
  username: string;
  password: string;
  remember?: boolean;
}

// Change password request interface
export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

// Update profile request interface
export interface UpdateProfileRequest {
  name?: string;
  email?: string;
  phone?: string;
  department?: string;
}

// Refresh token request interface
export interface RefreshTokenRequest {
  refresh_token: string;
}

/**
 * User login
 */
export const login = async (data: LoginRequest): Promise<AuthResponse> => {
  // OAuth2PasswordRequestForm expects form-data format
  const formData = new URLSearchParams();
  formData.append('username', data.username);
  formData.append('password', data.password);

  const response = await post<AuthResponse>('/auth/login', formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });

  return response;
};

/**
 * User logout
 */
export const logout = async (): Promise<void> => {
  return post<void>('/auth/logout');
};

/**
 * Refresh access token
 */
export const refreshToken = async (data: RefreshTokenRequest): Promise<AuthResponse> => {
  return post<AuthResponse>('/auth/refresh', data);
};

/**
 * Get current user info
 */
export const getCurrentUser = async (): Promise<UserInfo> => {
  return get<UserInfo>('/auth/me');
};

/**
 * Update user profile
 */
export const updateProfile = async (data: UpdateProfileRequest): Promise<UserInfo> => {
  return post<UserInfo>('/auth/profile', data);
};

/**
 * Change password
 */
export const changePassword = async (data: ChangePasswordRequest): Promise<void> => {
  return post<void>('/auth/password', data);
};

/**
 * Upload user avatar
 */
export const uploadAvatar = async (file: File): Promise<{ avatarUrl: string }> => {
  const formData = new FormData();
  formData.append('avatar', file);

  return post<{ avatarUrl: string }>('/auth/avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * Get user permissions
 */
export const getUserPermissions = async (): Promise<string[]> => {
  return get<string[]>('/auth/permissions');
};